import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  fetchLoginsRequest,
  fetchLoginRequest,
  createLoginRequest,
  updateLoginRequest,
  deleteLoginRequest,
} from '_/api/logins/logins.api';
import { ILogin, ILoginForm } from '_/types/logins/logins.types';
import { notification } from 'antd';

const LOGINS_QUERY_KEY = (loginPoolId: string) => ['logins', loginPoolId];
const LOGIN_QUERY_KEY = (loginId: string) => ['login', loginId];

export const useLogins = (loginPoolId: string, enabled = true): UseQueryResult<ILogin[], Error> => {
  return useQuery({
    queryKey: LOGINS_QUERY_KEY(loginPoolId),
    queryFn: () => fetchLoginsRequest(loginPoolId),
    enabled: enabled && !!loginPoolId,
  });
};

export const useLogin = (loginId: string, enabled = true): UseQueryResult<ILogin, Error> => {
  return useQuery({
    queryKey: LOGIN_QUERY_KEY(loginId),
    queryFn: () => fetchLoginRequest(loginId),
    enabled: enabled && !!loginId,
  });
};

interface ICreateLoginParams {
  loginPoolId: string;
  data: ILoginForm;
  orgSlug: string;
  projectSlug: string;
  loginPoolSlug: string;
}

export const useCreateLogin = (): UseMutationResult<ILogin, Error, ICreateLoginParams> => {
  const queryClient = useQueryClient();
  const history = useNavigate();

  return useMutation({
    mutationFn: ({ loginPoolId, data }: ICreateLoginParams) => createLoginRequest(loginPoolId, data),
    onSuccess: (_, { loginPoolId, orgSlug, projectSlug, loginPoolSlug }) => {
      void queryClient.invalidateQueries({ queryKey: LOGINS_QUERY_KEY(loginPoolId) });
      void history({
        to: `/orgs/${orgSlug}/projects/${projectSlug}/loginpools/${loginPoolSlug}/logins`,
      });
    },
    onError: (error: any) => {
      notification.error({
        message: error?.message || 'Failed to create login',
      });
    },
  });
};

interface IUpdateLoginParams {
  loginId: string;
  loginPoolId: string;
  data: ILoginForm;
  orgSlug: string;
  projectSlug: string;
  loginPoolSlug: string;
}

export const useUpdateLogin = (): UseMutationResult<ILogin, Error, IUpdateLoginParams> => {
  const queryClient = useQueryClient();
  const history = useNavigate();

  return useMutation({
    mutationFn: ({ loginId, data }: IUpdateLoginParams) => updateLoginRequest(loginId, data),
    onSuccess: (_, { loginPoolId, loginId, orgSlug, projectSlug, loginPoolSlug }) => {
      void queryClient.invalidateQueries({ queryKey: LOGINS_QUERY_KEY(loginPoolId) });
      void queryClient.invalidateQueries({ queryKey: LOGIN_QUERY_KEY(loginId) });
      void history({
        to: `/orgs/${orgSlug}/projects/${projectSlug}/loginpools/${loginPoolSlug}/logins`,
      });
    },
    onError: (error: any) => {
      notification.error({
        message: error?.message || 'Failed to update login',
      });
    },
  });
};

interface IDeleteLoginParams {
  loginId: string;
  loginPoolId: string;
}

export const useDeleteLogin = (): UseMutationResult<void, Error, IDeleteLoginParams> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ loginId }: IDeleteLoginParams) => deleteLoginRequest(loginId),
    onSuccess: (_, { loginPoolId }) => {
      void queryClient.invalidateQueries({ queryKey: LOGINS_QUERY_KEY(loginPoolId) });
    },
    onError: (error: any) => {
      notification.error({
        message: error?.message || 'Failed to delete login',
      });
    },
  });
};
